import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/** Shared procurement / RFQ blue palette (matches Create RFQ). */
export const procurementButtonStyles = {
  base: cn(
    'inline-flex items-center justify-center gap-2 font-bold transition-all shadow-md',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0088cc]/60 focus-visible:ring-offset-2',
    'focus-visible:ring-offset-[hsl(var(--background))]',
    'active:scale-[0.98] disabled:pointer-events-none'
  ),
  primary: cn(
    'bg-[#0088cc] text-white border border-[#0088cc]',
    'hover:bg-[#0077b3] hover:border-[#0077b3] hover:shadow-lg',
    'disabled:bg-[#334155] disabled:border-[#475569] disabled:text-[#0088cc]/45',
    'disabled:shadow-none disabled:cursor-not-allowed disabled:opacity-100'
  ),
  action: cn(
    'bg-[#0088cc] text-white border border-[#0077b3]',
    'hover:bg-[#0077b3] hover:border-[#006699] hover:shadow-lg',
    'disabled:bg-[#334155] disabled:border-[#475569] disabled:text-[#0088cc]/45',
    'disabled:shadow-none disabled:cursor-not-allowed disabled:opacity-100'
  ),
  sizeDefault: 'px-6 py-2.5 text-sm rounded-xl',
  sizeMd: 'px-6 py-3 text-xs rounded-xl uppercase tracking-wider',
  sizeWide: 'px-8 py-3 text-xs rounded-xl uppercase tracking-[0.1em]',
  sizeCompact: 'px-4 py-2 text-sm rounded-lg font-semibold',
  sizeModal: 'px-6 py-2.5 text-xs rounded-xl uppercase tracking-wider',
} as const;

export type ProcurementButtonVariant = 'primary' | 'action';
export type ProcurementButtonSize = 'default' | 'md' | 'wide' | 'compact' | 'modal';

const sizeClasses: Record<ProcurementButtonSize, string> = {
  default: procurementButtonStyles.sizeDefault,
  md: procurementButtonStyles.sizeMd,
  wide: procurementButtonStyles.sizeWide,
  compact: procurementButtonStyles.sizeCompact,
  modal: procurementButtonStyles.sizeModal,
};

const variantClasses: Record<ProcurementButtonVariant, string> = {
  primary: procurementButtonStyles.primary,
  action: procurementButtonStyles.action,
};

export type ProcurementActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ProcurementButtonVariant;
  size?: ProcurementButtonSize;
  icon?: ReactNode;
};

/**
 * Blue-themed action button for procurement / RFQ flows.
 * Visible on dark backgrounds; matches Create RFQ styling.
 */
export const ProcurementActionButton = forwardRef<HTMLButtonElement, ProcurementActionButtonProps>(
  function ProcurementActionButton(
    { className, variant = 'action', size = 'default', icon, children, disabled, ...props },
    ref
  ) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          procurementButtonStyles.base,
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {icon}
        {children}
      </button>
    );
  }
);

/** Primary CTA (Create RFQ, Apply, Submit). */
export const ProcurementPrimaryButton = forwardRef<HTMLButtonElement, Omit<ProcurementActionButtonProps, 'variant'>>(
  function ProcurementPrimaryButton(props, ref) {
    return <ProcurementActionButton ref={ref} variant="primary" {...props} />;
  }
);

/** Class helper when a native button must keep custom structure (e.g. file input trigger). */
export function procurementActionButtonClassName(
  options: { variant?: ProcurementButtonVariant; size?: ProcurementButtonSize; className?: string } = {}
): string {
  const { variant = 'action', size = 'default', className } = options;
  return cn(procurementButtonStyles.base, variantClasses[variant], sizeClasses[size], className);
}
